import { IAnyObject } from '../../interfaces'
import { convertToBooleanProperty, TBooleanProperty } from '../../types/TBooleanProperty'
import { convertToLocationProperty, TLocationProperty } from '../../types/TLocationProperty'
import { convertToNumberProperty, TNumberProperty } from '../../types/TNumberProperty'
import State from '../State'

export class PT300State extends State {
    public temperature_c: TNumberProperty
    public voltage: TNumberProperty
    public humidity: TNumberProperty
    public battery: TNumberProperty
    public rssi: TNumberProperty
    public pressure: TNumberProperty
    public gpsjamming: TNumberProperty
    public shock: TNumberProperty
    public orientation: TNumberProperty
    public tempProbe: TNumberProperty
    public light: TNumberProperty
    public ir: TNumberProperty
    public doorOpen: TBooleanProperty
    public battId: string
    public cycle: TNumberProperty

    static readonly BAD_VALUES: string[] = ['-999', '-9999']
    static readonly BAD_VALUES_RSSI: string[] = ['-999', '-9999', '999']

    static readonly DBKeys: { [key: string]: string } = {
        VOLTAGE: 'REPORT:sensors:VOLTAGE',
        CAP_REMAINING: 'REPORT:sensors:CAP_REMAINING',
        HUMIDITY: 'REPORT:sensors:HUMIDITY',
        TEMPERATURE_C: 'REPORT:sensors:TEMPERATURE_C',
        RSSI: 'REPORT:network:RSSI',
        PRESSURE: 'REPORT:sensors:PRESSURE',
        GPSJAMMING: 'REPORT:sensors:GPSJAMMING',
        SHOCK: 'REPORT:sensors:SHOCK',
        ORIENTATION: 'REPORT:sensors:ORIENTATION',
        TEMPPROBE: 'REPORT:sensors:TEMPPROBE',
        LIGHT: 'REPORT:sensors:LIGHT',
        IRLIGHT: 'REPORT:sensors:IRLIGHT',
        INPUT: 'REPORT:sensors:INPUT',
        CYCLE: 'REPORT:sensors:CYCLE'
    }

    constructor(data?: { [key: string]: string }) {
        super(data)
        if (data) {
            const epoch = data['Epoch']
            this.voltage = convertToNumberProperty(
                data[PT300State.DBKeys.VOLTAGE],
                epoch || data[`${PT300State.DBKeys.VOLTAGE}:clock`],
                1,
                PT300State.BAD_VALUES
            )
            if (data['battId']) {
                this.battId = data['battId']
            }
            this.battery = convertToNumberProperty(
                data[PT300State.DBKeys.CAP_REMAINING]
                    ? PT300State.battCalc(data[PT300State.DBKeys.CAP_REMAINING], this.battId)
                    : '',
                epoch || data[`${PT300State.DBKeys.CAP_REMAINING}:clock`],
                1,
                PT300State.BAD_VALUES
            )
            this.humidity = convertToNumberProperty(
                data[PT300State.DBKeys.HUMIDITY],
                epoch || data['REPORT:sensors:HUMIDITY:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.temperature_c = convertToNumberProperty(
                data[PT300State.DBKeys.TEMPERATURE_C],
                epoch || data['REPORT:sensors:TEMPERATURE_C:clock'],
                10,
                PT300State.BAD_VALUES
            )
            this.rssi = convertToNumberProperty(
                data[PT300State.DBKeys.RSSI],
                epoch || data['REPORT:network:RSSI:clock'],
                -1,
                PT300State.BAD_VALUES_RSSI
            )
            this.pressure = convertToNumberProperty(
                data[PT300State.DBKeys.PRESSURE],
                epoch || data['REPORT:sensors:PRESSURE:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.gpsjamming = convertToNumberProperty(
                data[PT300State.DBKeys.GPSJAMMING],
                epoch || data['REPORT:sensors:GPSJAMMING:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.shock = convertToNumberProperty(
                data[PT300State.DBKeys.SHOCK],
                epoch || data['REPORT:sensors:SHOCK:clock'],
                1000,
                PT300State.BAD_VALUES
            )
            this.orientation = convertToNumberProperty(
                data[PT300State.DBKeys.ORIENTATION] ? PT300State.tiltCalc(data[PT300State.DBKeys.ORIENTATION]) : '',
                epoch || data['REPORT:sensors:ORIENTATION:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.tempProbe = convertToNumberProperty(
                data[PT300State.DBKeys.TEMPPROBE] ? PT300State.KtoC(data[PT300State.DBKeys.TEMPPROBE]) : '',
                epoch || data['REPORT:sensors:TEMPPROBE:clock'],
                10,
                PT300State.BAD_VALUES
            )
            this.light = convertToNumberProperty(
                data[PT300State.DBKeys.LIGHT] ? PT300State.adcCalc(data[PT300State.DBKeys.LIGHT]) : '',
                epoch || data['REPORT:sensors:LIGHT:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.ir = convertToNumberProperty(
                data[PT300State.DBKeys.IRLIGHT] ? PT300State.adcCalc(data[PT300State.DBKeys.IRLIGHT]) : '',
                epoch || data['REPORT:sensors:IRLIGHT:clock'],
                1,
                PT300State.BAD_VALUES
            )
            this.doorOpen = convertToBooleanProperty(
                data[PT300State.DBKeys.INPUT],
                epoch || data['REPORT:sensors:INPUT:clock']
            )
            this.cycle = convertToNumberProperty(
                data[PT300State.DBKeys.CYCLE],
                epoch || data['REPORT:sensors:CYCLE:clock']
            )
        }
    }

    public update(data: IAnyObject): void {
        super.update(data)
        const epoch = data['Epoch'] as string

        if (data[PT300State.DBKeys.VOLTAGE]) {
            this.voltage = convertToNumberProperty(
                data[PT300State.DBKeys.VOLTAGE] as string,
                epoch || (data['REPORT:sensors:VOLTAGE:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.CAP_REMAINING]) {
            this.battery = convertToNumberProperty(
                data[PT300State.DBKeys.CAP_REMAINING]
                    ? PT300State.battCalc(data[PT300State.DBKeys.CAP_REMAINING] as string, this.battId)
                    : '',
                epoch || (data[`${PT300State.DBKeys.CAP_REMAINING}:clock`] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.HUMIDITY]) {
            this.humidity = convertToNumberProperty(
                data[PT300State.DBKeys.HUMIDITY] as string,
                epoch || (data['REPORT:sensors:HUMIDITY:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.TEMPERATURE_C]) {
            this.temperature_c = convertToNumberProperty(
                data[PT300State.DBKeys.TEMPERATURE_C] as string,
                epoch || (data['REPORT:sensors:TEMPERATURE_C:clock'] as string),
                10,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.RSSI]) {
            this.rssi = convertToNumberProperty(
                data[PT300State.DBKeys.RSSI] as string,
                epoch || (data['REPORT:network:RSSI:clock'] as string),
                -1,
                PT300State.BAD_VALUES_RSSI
            )
        }
        if (data[PT300State.DBKeys.PRESSURE]) {
            this.pressure = convertToNumberProperty(
                data[PT300State.DBKeys.PRESSURE] as string,
                epoch || (data['REPORT:sensors:PRESSURE:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.GPSJAMMING]) {
            this.gpsjamming = convertToNumberProperty(
                data[PT300State.DBKeys.GPSJAMMING] as string,
                epoch || (data['REPORT:sensors:GPSJAMMING:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.SHOCK]) {
            this.shock = convertToNumberProperty(
                data[PT300State.DBKeys.SHOCK] as string,
                epoch || (data['REPORT:sensors:SHOCK:clock'] as string),
                1000,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.ORIENTATION]) {
            this.orientation = convertToNumberProperty(
                data[PT300State.DBKeys.ORIENTATION]
                    ? PT300State.tiltCalc(data[PT300State.DBKeys.ORIENTATION] as string)
                    : '',
                epoch || (data['REPORT:sensors:ORIENTATION:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.TEMPPROBE]) {
            this.tempProbe = convertToNumberProperty(
                data[PT300State.DBKeys.TEMPPROBE] ? PT300State.KtoC(data[PT300State.DBKeys.TEMPPROBE] as string) : '',
                epoch || (data['REPORT:sensors:TEMPPROBE:clock'] as string),
                10,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.LIGHT]) {
            this.light = convertToNumberProperty(
                data[PT300State.DBKeys.LIGHT] ? PT300State.adcCalc(data[PT300State.DBKeys.LIGHT] as string) : '',
                epoch || (data['REPORT:sensors:LIGHT:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.IRLIGHT]) {
            this.ir = convertToNumberProperty(
                data[PT300State.DBKeys.IRLIGHT] ? PT300State.adcCalc(data[PT300State.DBKeys.IRLIGHT] as string) : '',
                epoch || (data['REPORT:sensors:IRLIGHT:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
        if (data[PT300State.DBKeys.INPUT]) {
            this.doorOpen = convertToBooleanProperty(
                data[PT300State.DBKeys.INPUT] as string,
                epoch || (data['REPORT:sensors:INPUT:clock'] as string)
            )
        }
        if (data[PT300State.DBKeys.CYCLE]) {
            this.cycle = convertToNumberProperty(
                data[PT300State.DBKeys.CYCLE] as string,
                epoch || (data['REPORT:sensors:CYCLE:clock'] as string),
                1,
                PT300State.BAD_VALUES
            )
        }
    }

    public static tiltCalc(orientation: string): string {
        const xyz = orientation.split(',')
        if (orientation === '180') {
            return ''
        }

        if (xyz.length === 1) {
            return orientation
        }

        const invalidValue = xyz.find(v => PT300State.BAD_VALUES.indexOf(v) !== -1)
        if (invalidValue) {
            return ''
        }

        const x = parseFloat(xyz[0].trim())
        const y = parseFloat(xyz[1].trim())
        const z = parseFloat(xyz[2].trim())

        let tilt

        if (z === 0) {
            tilt = 90

            return tilt.toString()
        }

        const resultant = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2))
        const div = resultant / z
        // Math.atan returns the arctangent of a number as a value between -PI/2 and PI/2 radians.
        const mytilt = Math.atan(div)

        tilt = mytilt * (180 / Math.PI)

        if (z < 0) {
            tilt = Math.ceil(tilt) + 180
        }

        return Math.floor(tilt).toString()
    }

    public static adcCalc(val: string): string {
        if (PT300State.BAD_VALUES.indexOf(val) !== -1) {
            return ''
        }

        return ((parseFloat(val) / 65535) * 100).toFixed(0)
    }

    public static KtoC(val: string): string {
        if (PT300State.BAD_VALUES.indexOf(val) !== -1) {
            return ''
        }

        return (parseFloat(val) - 2732).toFixed(1)
    }

    public static battCalc(val: string, battId?: string): string {
        if (PT300State.BAD_VALUES.indexOf(val) !== -1) {
            return ''
        }
        const divider = battId && battId.startsWith('GP331A') ? 44 : 37.6
        const capacity = parseFloat(val) / divider

        return (capacity > 100 ? 100 : capacity).toFixed(1)
    }

    public static parseHistory(
        data: IAnyObject,
        sensor: string,
        battId?: string
    ): TNumberProperty | TBooleanProperty | TLocationProperty {
        switch (sensor) {
            case PT300State.DBKeys.GPSJAMMING:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            case PT300State.DBKeys.ORIENTATION:
                return convertToNumberProperty(
                    data.value ? PT300State.tiltCalc(data.value as string) : '',
                    data.epoch as string,
                    1,
                    PT300State.BAD_VALUES
                )
            case PT300State.DBKeys.PRESSURE:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            case PT300State.DBKeys.RSSI:
                return convertToNumberProperty(
                    data.value as string,
                    data.epoch as string,
                    -1,
                    PT300State.BAD_VALUES_RSSI
                )
            case PT300State.DBKeys.TEMPERATURE_C:
                return convertToNumberProperty(data.value as string, data.epoch as string, 10, PT300State.BAD_VALUES)
            case 'location':
                return convertToLocationProperty(data as { [key: string]: string }, data.epoch as string)
            case PT300State.DBKeys.HUMIDITY:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            case PT300State.DBKeys.VOLTAGE:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            case PT300State.DBKeys.TEMPPROBE:
                return convertToNumberProperty(
                    data.value ? PT300State.KtoC(data.value as string) : '',
                    data.epoch as string,
                    10,
                    PT300State.BAD_VALUES
                )
            case PT300State.DBKeys.LIGHT:
                return convertToNumberProperty(
                    data.value ? PT300State.adcCalc(data.value as string) : '',
                    data.epoch as string,
                    1,
                    PT300State.BAD_VALUES
                )
            case PT300State.DBKeys.IRLIGHT:
                return convertToNumberProperty(
                    data.value ? PT300State.adcCalc(data.value as string) : '',
                    data.epoch as string,
                    1,
                    PT300State.BAD_VALUES
                )
            case PT300State.DBKeys.SHOCK:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1000, PT300State.BAD_VALUES)
            case PT300State.DBKeys.INPUT:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            case PT300State.DBKeys.CAP_REMAINING:
                return convertToNumberProperty(
                    data.value ? PT300State.battCalc(data.value as string, battId) : '',
                    data.epoch as string,
                    1,
                    PT300State.BAD_VALUES
                )
            case PT300State.DBKeys.CYCLE:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
            default:
                return convertToNumberProperty(data.value as string, data.epoch as string, 1, PT300State.BAD_VALUES)
        }
    }

    public static parseSensor(sensor: string, forward: boolean): string {
        if (forward) {
            switch (sensor) {
                case PT300State.DBKeys.GPSJAMMING:
                    return 'gpsjamming'
                case PT300State.DBKeys.ORIENTATION:
                    return 'orientation'
                case PT300State.DBKeys.PRESSURE:
                    return 'pressure'
                case PT300State.DBKeys.RSSI:
                    return 'rssi'
                case PT300State.DBKeys.TEMPERATURE_C:
                    return 'temperature_c'
                case PT300State.DBKeys.HUMIDITY:
                    return 'humidity'
                case PT300State.DBKeys.VOLTAGE:
                    return 'voltage'
                case PT300State.DBKeys.TEMPPROBE:
                    return 'tempProbe'
                case PT300State.DBKeys.LIGHT:
                    return 'light'
                case PT300State.DBKeys.IRLIGHT:
                    return 'ir'
                case PT300State.DBKeys.SHOCK:
                    return 'shock'
                case PT300State.DBKeys.INPUT:
                    return 'doorOpen'
                case PT300State.DBKeys.CAP_REMAINING:
                    return 'battery'
                case PT300State.DBKeys.CYCLE:
                    return 'cycle'
                default:
                    return sensor
            }
        } else {
            switch (sensor) {
                case 'gpsjamming':
                    return PT300State.DBKeys.GPSJAMMING
                case 'orientation':
                    return PT300State.DBKeys.ORIENTATION
                case 'pressure':
                    return PT300State.DBKeys.PRESSURE
                case 'rssi':
                    return PT300State.DBKeys.RSSI
                case 'temperature_c':
                    return PT300State.DBKeys.TEMPERATURE_C
                case 'humidity':
                    return PT300State.DBKeys.HUMIDITY
                case 'voltage':
                    return PT300State.DBKeys.VOLTAGE
                case 'tempProbe':
                    return PT300State.DBKeys.TEMPPROBE
                case 'light':
                    return PT300State.DBKeys.LIGHT
                case 'ir':
                    return PT300State.DBKeys.IRLIGHT
                case 'shock':
                    return PT300State.DBKeys.SHOCK
                case 'doorOpen':
                    return PT300State.DBKeys.INPUT
                case 'battery':
                    return PT300State.DBKeys.CAP_REMAINING
                case 'cycle':
                    return PT300State.DBKeys.CYCLE
                default:
                    return sensor
            }
        }
    }
}
